import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import {
  BuildingLibraryIcon,
  CogIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  CalendarIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

const AutoReconciliationPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.autoReconciliationPage.${key}`, opts);

  const [isReconciliating, setIsReconciliating] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [openSettings, setOpenSettings] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [reconciliationResult, setReconciliationResult] = useState(null);
  
  // Options de rapprochement
  const [reconciliationOptions, setReconciliationOptions] = useState({
    matchByReference: true,
    matchByAmount: true,
    matchByDate: true,
    matchByInvoiceRef: true,
    matchByCheckNumber: true, // Nouvelle option pour les numéros de chèque
    dateToleranceDays: 3,
    createSuggestions: true,
  });

  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reconciliationStats, setReconciliationStats] = useState({});

  const isAccountActive = (account) =>
    account?.active === true || account?.status === 'active' || account?.status === 'Active';

  const loadBankAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/accounts');
      setAccounts(normalizeAndSyncBankAccounts(data));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const loadReconciliationStats = useCallback(async (accountIds) => {
    if (!accountIds.length) {
      setReconciliationStats({});
      return;
    }
    try {
      const accountingRows = await localApiAdapter.get('/banking/accounting', { status: 'unreconciled' });
      const accountingList = Array.isArray(accountingRows) ? accountingRows : [];
      const stats = {};

      await Promise.all(accountIds.map(async (accountId) => {
        const statements = await localApiAdapter.get('/banking/statements', {
          account_id: accountId,
          reconciled: 'false',
        });
        const bankRows = Array.isArray(statements) ? statements : [];
        const accountingForAccount = accountingList.filter(
          (entry) => String(entry.account_id) === String(accountId),
        );
        stats[accountId] = {
          bank: { unreconciled: bankRows.length },
          accounting: { unreconciled: accountingForAccount.length },
          totalEntries: bankRows.length + accountingForAccount.length,
          matchedEntries: 0,
          unmatchedEntries: bankRows.length + accountingForAccount.length,
          discrepancies: 0,
          lastReconciliation: null,
          matchRate: 0,
        };
      }));

      setReconciliationStats(stats);
    } catch (error) {
      console.error('Error loading reconciliation stats:', error);
      setReconciliationStats({});
    }
  }, []);

  useEffect(() => {
    if (accounts.length > 0 && selectedAccounts.length === 0) {
      const activeAccountIds = accounts.filter(isAccountActive).map((account) => account.id);
      setSelectedAccounts(activeAccountIds);
    }
  }, [accounts, selectedAccounts.length]);

  useEffect(() => {
    if (selectedAccounts.length > 0) {
      loadReconciliationStats(selectedAccounts);
    } else {
      setReconciliationStats({});
    }
  }, [selectedAccounts, loadReconciliationStats]);

  const getUnreconciledCount = (accountId) => {
    if (!reconciliationStats || !reconciliationStats[accountId]) return { bank: 0, accounting: 0 };
    
    const stats = reconciliationStats[accountId];
    return {
      bank: stats.bank?.unreconciled || 0,
      accounting: stats.accounting?.unreconciled || 0,
    };
  };

  // Real API auto reconciliation function
  const performAutoReconciliation = async (accountId) => {
    try {
      // Step 1: Create a new reconciliation session for this account
      const periodEnd = new Date();
      const periodStart = new Date();
      periodStart.setMonth(periodStart.getMonth() - 1); // Last month
      
      const createResult = await localApiAdapter.post('/banking/reconciliation', {
        account_id: Number(accountId),
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0],
        opening_balance: 0,
        closing_balance: 0,
        statement_balance: 0,
        book_balance: 0,
      });

      const reconciliationId = createResult?.id ?? createResult?.data?.id;
      if (!reconciliationId) {
        throw new Error(p('error_create_session'));
      }

      const result = await localApiAdapter.post(`/banking/reconciliation/${reconciliationId}/auto-reconcile`, {
        options: {
          by_reference: reconciliationOptions.matchByReference,
          by_amount: reconciliationOptions.matchByAmount,
          by_date: reconciliationOptions.matchByDate,
          by_check_number: reconciliationOptions.matchByCheckNumber,
          partial_matching: true,
          date_tolerance: reconciliationOptions.dateToleranceDays,
          amount_tolerance: 0.01,
          create_suggestions: reconciliationOptions.createSuggestions,
          min_confidence: 60,
        },
      });
      
      // Format result to match UI expectations
      const formattedResult = {
        accountId,
        reconciliationId,
        matched: result?.matched || 0,
        unmatched: result?.unmatched || 0,
        discrepancies: result?.discrepancies || 0,
        suggestions: result?.suggestions || 0,
        confidenceBreakdown: result?.confidence_breakdown || { high: 0, medium: 0, low: 0 },
        matchDetails: result?.match_details || [],
        processingTime: result?.processing_time || '0ms',
        status: 'success',
      };

      setReconciliationResult((prevResults) => ({
        ...(prevResults || {}),
        [accountId]: formattedResult,
      }));
      
      await loadReconciliationStats(selectedAccounts);
      return formattedResult;
    } catch (error) {
      console.error(`Error for account ${accountId}:`, error);
      toast.error(p('toast_account_error', { accountId }));
      
      const errorResult = { error: true, message: error.message || p('api_error_fallback'), accountId };
      setReconciliationResult((prevResults) => ({
        ...(prevResults || {}),
        [accountId]: errorResult,
      }));
      
      throw error;
    }
  };

  // Effectuer un rapprochement automatique pour tous les comptes sélectionnés
  const handleReconciliation = async () => {
    setIsReconciliating(true);
    setReconciliationResult({});
    
    try {
      const promises = selectedAccounts.map(accountId => 
        performAutoReconciliation(accountId)
      );
      
      await Promise.all(promises);
      setShowResultModal(true);
      toast.success(p('toast_complete'));
    } catch (error) {
      console.error('Error during automatic reconciliation:', error);
      toast.error(p('toast_auto_error'));
    } finally {
      setIsReconciliating(false);
    }
  };

  // Gérer les changements dans les options
  const handleOptionChange = (option, value) => {
    setReconciliationOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };
  
  // Gérer la sélection d'un compte
  const toggleAccountSelection = (accountId) => {
    if (selectedAccounts.includes(accountId)) {
      setSelectedAccounts(selectedAccounts.filter(id => id !== accountId));
    } else {
      setSelectedAccounts([...selectedAccounts, accountId]);
    }
  };

  // sélectionner ou Désélectionner tous les comptes actifs
  const toggleAllAccounts = () => {
    if (!accounts) return;
    
    if (selectedAccounts.length === accounts.filter(isAccountActive).length) {
      // Si tous les comptes sont sélectionnés, tout Désélectionner
      setSelectedAccounts([]);
    } else {
      // Sinon, sélectionner tous les comptes actifs
      const activeAccountIds = accounts
        .filter(isAccountActive)
        .map(account => account.id);
      setSelectedAccounts(activeAccountIds);
    }
  };

  // Calculer le nombre total d'écritures non réconciliées
  const calculateTotalUnreconciled = () => {
    if (!reconciliationStats) return { bank: 0, accounting: 0 };
    
    return selectedAccounts.reduce((total, accountId) => {
      const counts = getUnreconciledCount(accountId);
      return {
        bank: total.bank + counts.bank,
        accounting: total.accounting + counts.accounting
      };
    }, { bank: 0, accounting: 0 });
  };
  
  // Calculer le nombre total de rapprochements effectués
  const calculateTotalReconciliations = () => {
    if (!reconciliationResult) return 0;
    
    return Object.values(reconciliationResult).reduce((total, result) => {
      return total + (result.matched || 0);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">{p('title')}</h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setOpenSettings(!openSettings)}
            className="bg-white/10 text-white px-5 py-3 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <CogIcon className="h-5 w-5" />
            <span>{p('toggle_options')}</span>
            {openSettings ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
          </button>
        </div>
      </div>

      {/* Options de rapprochement */}
      {openSettings && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-white/5 rounded-xl border border-white/20 p-6 overflow-hidden"
        >
          <h2 className="text-lg font-semibold text-white mb-4">{p('options_title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="matchByReference"
                checked={reconciliationOptions.matchByReference}
                onChange={(e) => handleOptionChange('matchByReference', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="matchByReference" className="ml-2 text-white">
                {p('match_by_reference')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.matchByReference ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="matchByAmount"
                checked={reconciliationOptions.matchByAmount}
                onChange={(e) => handleOptionChange('matchByAmount', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="matchByAmount" className="ml-2 text-white">
                {p('match_by_amount')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.matchByAmount ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="matchByDate"
                checked={reconciliationOptions.matchByDate}
                onChange={(e) => handleOptionChange('matchByDate', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="matchByDate" className="ml-2 text-white">
                {p('match_by_date')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.matchByDate ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="matchByInvoiceRef"
                checked={reconciliationOptions.matchByInvoiceRef}
                onChange={(e) => handleOptionChange('matchByInvoiceRef', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="matchByInvoiceRef" className="ml-2 text-white">
                {p('match_by_invoice_ref')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.matchByInvoiceRef ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="matchByCheckNumber"
                checked={reconciliationOptions.matchByCheckNumber}
                onChange={(e) => handleOptionChange('matchByCheckNumber', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="matchByCheckNumber" className="ml-2 text-white">
                {p('match_by_check_number')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.matchByCheckNumber ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="createSuggestions"
                checked={reconciliationOptions.createSuggestions}
                onChange={(e) => handleOptionChange('createSuggestions', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="createSuggestions" className="ml-2 text-white">
                {p('create_suggestions')}
              </label>
              <div className="ml-auto text-white/50">
                <CheckCircleIcon className={`h-5 w-5 ${reconciliationOptions.createSuggestions ? 'text-blue-500' : 'text-white/20'}`} />
              </div>
            </div>
            
            {reconciliationOptions.matchByDate && (
              <div className="col-span-2 mt-2">
                <label htmlFor="dateToleranceDays" className="block w-full text-white mb-2 font-medium text-right">
                  {p('date_tolerance_label')} <span className="font-medium">{reconciliationOptions.dateToleranceDays}</span>
                </label>
                <input
                  type="range"
                  id="dateToleranceDays"
                  min={0}
                  max={10}
                  step={1}
                  value={reconciliationOptions.dateToleranceDays}
                  onChange={(e) => handleOptionChange('dateToleranceDays', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>0</span>
                  <span>5</span>
                  <span>10</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Liste des comptes */}
      <div className="bg-white/5 rounded-xl border border-white/20 p-6 overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">{p('accounts_to_reconcile')}</h2>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleAllAccounts}
              className="text-white/80 hover:text-white text-sm flex items-center"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              {selectedAccounts.length === (accounts?.filter(isAccountActive).length || 0) ? p('deselect_all') : p('select_all')}
            </button>
            
            <button
              onClick={() => loadBankAccounts()}
              className="text-white/80 hover:text-white text-sm flex items-center"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              {p('refresh')}
            </button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : accounts && accounts.length > 0 ? (
          <div className="space-y-3">
            {accounts.filter(isAccountActive).map((account) => {
              const unreconciledCount = getUnreconciledCount(account.id);
              const isSelected = selectedAccounts.includes(account.id);
              const accountName = account.account_name || account.name || p('account_fallback', { id: account.id });
              const bankLabel = account.bank_name || account.bank || '';
              const accountNumber = account.account_number || account.accountNumber || '';
              
              return (
                <div
                  key={account.id}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                    isSelected ? 'bg-white/10 border-blue-500/50'
                      : 'bg-white/5 border-white/10'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleAccountSelection(account.id)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mr-4"
                    />
                    
                    <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                      <BuildingLibraryIcon className="h-6 w-6 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-white font-medium">{accountName}</h3>
                      <div className="flex items-center mt-1 text-sm">
                        <span className="text-white/60">{bankLabel}</span>
                        {accountNumber ? (
                          <>
                            <span className="mx-2 text-white/30">·</span>
                            <span className="text-white/60">{accountNumber}</span>
                          </>
                        ) : null}
                        {account.wilaya && (
                          <>
                            <span className="mx-2 text-white/30">·</span>
                            <span className="text-white/60">{account.wilaya}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xs text-white/50">{p('bank_entries_label')}</div>
                      <div className="text-lg font-semibold text-white">{unreconciledCount.bank}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-xs text-white/50">{p('accounting_entries_label')}</div>
                      <div className="text-lg font-semibold text-white">{unreconciledCount.accounting}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10 text-white/60">
            <p>{p('no_accounts')}</p>
          </div>
        )}
      </div>
      
      {/* Règles de rapprochement personnalisées */}
      <div className="bg-white/5 rounded-xl border border-white/20 p-6 overflow-hidden">
        <h2 className="text-xl font-semibold text-white mb-4">{p('info_title')}</h2>
        
        <div className="flex flex-wrap gap-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/20 flex-1 min-w-[250px]">
            <div className="flex items-center space-x-2 text-white mb-3">
              <DocumentTextIcon className="h-5 w-5 text-blue-400" />
              <h3 className="font-medium">{p('documents_title')}</h3>
            </div>
            <ul className="space-y-1 text-white/80">
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-blue-400 mr-2"></span>
                {p('doc_proforma')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-blue-400 mr-2"></span>
                {p('doc_orders')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-blue-400 mr-2"></span>
                {p('doc_check')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-blue-400 mr-2"></span>
                {p('doc_tax')}
              </li>
            </ul>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 border border-white/20 flex-1 min-w-[250px]">
            <div className="flex items-center space-x-2 text-white mb-3">
              <ArrowsRightLeftIcon className="h-5 w-5 text-purple-400" />
              <h3 className="font-medium">{p('methods_title')}</h3>
            </div>
            <ul className="space-y-1 text-white/80">
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                {p('method_exact_amount')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                {p('method_doc_ref')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                {p('method_date_tolerance')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                {p('method_check_detection')}
              </li>
            </ul>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 border border-white/20 flex-1 min-w-[250px]">
            <div className="flex items-center space-x-2 text-white mb-3">
              <DocumentMagnifyingGlassIcon className="h-5 w-5 text-amber-400" />
              <h3 className="font-medium">{p('extra_info_title')}</h3>
            </div>
            <ul className="space-y-1 text-white/80">
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-amber-400 mr-2"></span>
                {p('extra_supplier')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-amber-400 mr-2"></span>
                {p('extra_vat')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-amber-400 mr-2"></span>
                {p('extra_debit_credit')}
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-amber-400 mr-2"></span>
                {p('extra_classification')}
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Bouton de rapprochement */}
      <div className="flex justify-center">
        <button
          onClick={handleReconciliation}
          disabled={isReconciliating || selectedAccounts.length === 0}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isReconciliating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              <span>{p('reconciling')}</span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="h-6 w-6" />
              <span>{p('start_reconciliation')}</span>
            </>
          )}
        </button>
      </div>

      {/* Modal des résultats */}
      {showResultModal && reconciliationResult && Object.keys(reconciliationResult).length > 0 && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setShowResultModal(false)} role="presentation" />
          
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10 p-8 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.35), 0 0 60px rgba(59, 130, 246, 0.15)' }}
          >
            <button 
              className="absolute right-4 top-4 text-white/50 hover:text-white"
              onClick={() => setShowResultModal(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            <h2 className="text-2xl font-bold text-white mb-6">{p('results_title')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                <div className="text-sm text-white/60">{p('accounts_processed')}</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {Object.keys(reconciliationResult).length}
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                <div className="text-sm text-white/60">{p('reconciliations_created')}</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {calculateTotalReconciliations()}
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                <div className="text-sm text-white/60">{p('remaining_entries')}</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {calculateTotalUnreconciled().bank + calculateTotalUnreconciled().accounting}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {Object.entries(reconciliationResult).map(([accountId, result]) => {
                const account = accounts?.find(acc => String(acc.id) === String(accountId));
                
                return (
                  <div key={accountId} className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="font-semibold text-white text-lg mb-4">
                      {account?.name || p('account_fallback', { id: accountId })}
                    </h3>
                    
                    {result.error ? (
                      <div className="mt-2 text-red-400 text-sm flex items-center">
                        <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                        {result.message || p('error_occurred')}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Stats rapides */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                            <div className="text-xs text-green-300/70">{p('stat_matched')}</div>
                            <div className="text-2xl font-bold text-green-300">{result.matched || 0}</div>
                          </div>
                          
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <div className="text-xs text-amber-300/70">{p('stat_unmatched')}</div>
                            <div className="text-2xl font-bold text-amber-300">{result.unmatched || 0}</div>
                          </div>
                          
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                            <div className="text-xs text-blue-300/70">{p('stat_suggestions')}</div>
                            <div className="text-2xl font-bold text-blue-300">{result.suggestions || 0}</div>
                          </div>
                          
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                            <div className="text-xs text-red-300/70">{p('stat_discrepancies')}</div>
                            <div className="text-2xl font-bold text-red-300">{result.discrepancies || 0}</div>
                          </div>
                        </div>
                        
                        {/* Confidence Breakdown */}
                        {result.confidenceBreakdown && (
                          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                            <h4 className="text-white font-medium mb-3 flex items-center">
                              <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-400" />
                              {p('confidence_title')}
                            </h4>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-white/70 text-sm">{p('confidence_high')}</span>
                                <span className="text-green-300 font-semibold">{result.confidenceBreakdown.high || 0}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/70 text-sm">{p('confidence_medium')}</span>
                                <span className="text-amber-300 font-semibold">{result.confidenceBreakdown.medium || 0}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/70 text-sm">{p('confidence_low')}</span>
                                <span className="text-red-300 font-semibold">{result.confidenceBreakdown.low || 0}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Match Details Preview */}
                        {result.matchDetails && result.matchDetails.length > 0 && (
                          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                            <h4 className="text-white font-medium mb-3">{p('match_details_title')}</h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {result.matchDetails.slice(0, 10).map((detail, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      detail.type === 'reference' ? 'bg-purple-500/20 text-purple-300' :
                                      detail.type === 'amount_date' ? 'bg-blue-500/20 text-blue-300' :
                                      detail.type === 'check' ? 'bg-green-500/20 text-green-300' :
                                      detail.type === 'partial' ? 'bg-amber-500/20 text-amber-300' :
                                      'bg-gray-500/20 text-gray-300'
                                    }`}>
                                      {detail.type}
                                    </span>
                                    <span className="text-white/60">
                                      {detail.count ? p('grouped_entries', { count: detail.count }) : p('simple_match')}
                                    </span>
                                  </div>
                                  <span className="text-white/80 font-medium">{detail.confidence}%</span>
                                </div>
                              ))}
                              {result.matchDetails.length > 10 && (
                                <div className="text-center text-white/50 text-xs pt-2">
                                  {p('more_matches', { count: result.matchDetails.length - 10 })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Temps de traitement */}
                        <div className="flex items-center justify-between text-sm text-white/60">
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {p('processing_time')}
                          </span>
                          <span className="text-white/80 font-medium">{result.processingTime || '0ms'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowResultModal(false)}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-xl transition-colors font-medium"
              >
                {p('close')}
              </button>
              <button
                onClick={() => {
                  setShowResultModal(false);
                  loadBankAccounts();
                  loadReconciliationStats(selectedAccounts);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all font-medium"
              >
                {p('refresh_data')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AutoReconciliationPage; 


